import {
  CellKeyDownEvent,
  ColumnApi,
  GridApi,
  GridOptions,
  GridReadyEvent,
  Module,
} from '@ag-grid-community/core'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { DOCUMENT } from '@angular/common'
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
} from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { EMPTY, from, merge, Subject, Subscription } from 'rxjs'
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  tap,
} from 'rxjs/operators'
import {
  HttpDialogListValuesService,
  ResolvedLists,
} from '../../services/http-dialog-list-values.service'
import {
  SaveFailure,
  saveFailureMessage,
} from '../../services/http-transaction-save.service'
import { TransactionSaveService } from '../../services/transaction-save.service'
import {
  TransactionSaveRequest,
  TransactionSaveResponse,
} from '../../models/transaction-save.model'
import {
  TRANSACTION_SAVE_COPY,
  TRANSACTION_SAVE_VALIDATION_COPY,
} from '../../models/transaction-action.model'
import { SaveAssemblyError } from '../../edit-transaction/transaction-save.assembly'
import {
  assembleBulkRequests,
  BulkSaveBatch,
} from '../../bulk-transaction/bulk-transaction-save'
import {
  BulkItemColumn,
  bulkItemColumns,
  BulkTransactionColumn,
  BulkTransactionRow,
  BulkTransactionViewModel,
} from '../../bulk-transaction/bulk-transaction.projection'
import {
  deriveFieldStatus,
  EditTransactionCollection,
  EditTransactionField,
  requiredRuleUnmet,
  requiredUnsatisfied,
} from '../../edit-transaction/edit-transaction.projection'
import {
  editValue,
  formatFieldValue,
  rawValueFrom,
} from '../../edit-transaction/format-field-value'
import {
  blankCellLog,
  LOOKUP_DEBOUNCE_MS,
  normalizeOptions,
} from '../../edit-transaction/list-options'
import {
  pickerLabel,
  ValuePicker,
  ValuePickerOption,
} from '../value-picker/value-picker'
import {
  BULK_CELL_EDITOR,
  BulkCellEditorComponent,
} from './bulk-cell-editor.component'
import {
  BULK_GUTTER_COLUMNS,
  BulkGridRow,
  bulkGridColumnDefs,
} from './bulk-grid-columns'
import {
  coercePastedValue,
  isSingleValue,
  parseClipboardGrid,
  toClipboardText,
} from './bulk-clipboard'
import {
  BulkRowVerdict,
  bulkRowCounts,
  bulkFooterMissing,
  bulkMissingLabel,
  bulkRowVerdict,
  BulkTally,
  bulkTallies,
} from './bulk-transaction-grid'

/** Which columns the grid shows. */
export type BulkFieldSet = 'required' | 'all'

/** The row height the design draws, and the heights of the band and header strips above it. */
const AG_ROW_HEIGHT = 38
const AG_BAND_HEIGHT = 26
const AG_HEADER_HEIGHT = 32

/** Room for ag-grid's own horizontal scrollbar, which the columns usually need. */
const AG_SCROLLBAR = 16

/**
 * The tallest the grid gets, however many transactions are selected.
 *
 * Twelve rows plus the two header strips, which keeps the whole dialog — header, summary bar, grid,
 * footer fields and action bar — inside a 900px screen. Past this ag-grid virtualises and the grid
 * scrolls, which is the point of it.
 */
const AG_MAX_HEIGHT = AG_BAND_HEIGHT + AG_HEADER_HEIGHT + 12 * AG_ROW_HEIGHT

/**
 * One request of a bulk save and where it stands (PVAL-26440).
 *
 * The body is FROZEN when the save is first pressed: a retry re-sends exactly what failed and never
 * re-assembles, because the grid may have moved on and the request is not idempotent — re-assembling
 * after a partial success would send rows that already created commands.
 */
export interface BulkSaveRequestState {
  accountId: string
  request: TransactionSaveRequest
  state: 'pending' | 'sent' | 'failed'
  /** What the 200 answered, once sent. */
  response?: TransactionSaveResponse
  /** Why it failed, once failed — `saveFailureMessage`. */
  failure?: SaveFailure
}

/**
 * The most rows one range gesture — copy, paste or Delete — will reach.
 *
 * A row a gesture touches is projected on first ask (~0.5ms, ~34KB) and then kept for the life of
 * the dialog: an edited row holds its edit in its projected fields, so the cache cannot evict. And
 * ag-grid reaches the whole grid in two keystrokes (Ctrl+A), so without a ceiling one of them over
 * 30,000 rows costs the 13 seconds and ~1GB the lazy projection exists to avoid. Copy cannot be
 * sliced to stay responsive either — the clipboard is writable only synchronously, inside the event.
 *
 * A selection past this is refused and reported, and is worked through in blocks of this size.
 */
const RANGE_GESTURE_MAX_ROWS = 1000

/**
 * What a cell's list is read from: the field itself, or the column of the line an Item cell
 * addresses. Both carry the same list properties, which is what lets one lookup serve them.
 */
type ListSource = Pick<
  EditTransactionField,
  'field' | 'listValues' | 'displayColumns' | 'listKeys' | 'minChars'
>

/** One pending lookup search: what was typed, into which cell, and under which account. */
interface LookupRequest {
  index: number
  /** The grid key of the cell. */
  field: string
  /** The name the endpoint is asked about — the column's own for an Item cell, whose grid key is not. */
  keyName: string
  query: string
  accountId: string
  anchor: HTMLElement
}

/** The Bulk Transactions grid (PVAL-26495). */
@Component({
  selector: 'hs-bulk-transaction-modal',
  templateUrl: './bulk-transaction-modal.component.html',
  styleUrls: ['./bulk-transaction-modal.component.scss'],
})
export class BulkTransactionModalComponent implements OnDestroy {
  private model: BulkTransactionViewModel | null = null

  /** Which columns are shown. */
  private set = 'required' as BulkFieldSet

  /**
   * True while any save request is in flight (PVAL-26440). The overlay covers the grid, the buttons
   * are disabled, and the host's `beforeDismiss` refuses ESC and the backdrop.
   */
  saving = false

  /**
   * The requests of the save in progress or last finished, each with its outcome. Empty until Save
   * is pressed, and emptied again when a save sent nothing at all (so the grid is editable again).
   */
  requests: BulkSaveRequestState[] = []

  /** Why Save sent nothing at all, when it was refused before any request (no account, a bad value). */
  saveError: string | null = null

  /** What the overlay says while a save is in flight. */
  readonly savingLabel = 'Saving…'

  /**
   * True while the selection's trades are being read from `trades/get` (PVAL-26492).
   *
   * One request covers the whole selection, so this is the grid settling once rather than per row.
   * Set by the host, which owns the fetch.
   */
  loading = false

  /**
   * The failed trade read, shown in `hs-dialog-error-details` below the fields — the same error box
   * the Create/Edit Transaction modal puts below Notes, and the same two inputs.
   *
   * Set by the host alongside {@link loading}. A failure does not blank the dialog: the
   * grid-projected values stay, and this says why the fuller ones did not arrive.
   */
  errorHeader: string | undefined
  errorDetails: unknown

  /**
   * The message shown ON the frozen dialog when the read failed.
   *
   * Set means frozen: `hs-loader` swaps its spinner for an error icon and this text, and the
   * `active` class below flips its `pointer-events` on so the overlay blocks the fields behind it.
   * That is the Create/Edit Transaction modal's own rule — a load that failed leaves the overlay up
   * rather than revealing a half-filled form the user could edit and try to save.
   */
  errorLabel: string | undefined

  /**
   * Whether the overlay is covering the dialog — still reading, or stopped on an error.
   *
   * One getter for both, because the overlay's behaviour is identical in each: it is visible and it
   * swallows clicks. Only its contents differ, and `hs-loader` decides that from `errorLabel`.
   */
  /** What the overlay says while the read is in flight. */
  readonly loadingLabel = 'Loading transactions…'

  get frozen(): boolean {
    return this.loading || this.saving || !!this.errorLabel
  }

  /**
   * Read-only once any request of a save has gone through (PVAL-26440): those rows have created
   * commands, so a fresh Save would create them again. Only "Retry failed" and "Close" remain.
   */
  get locked(): boolean {
    return this.requests.some(entry => entry.state === 'sent')
  }

  /** The requests whose last attempt failed. */
  get failedRequests(): BulkSaveRequestState[] {
    return this.requests.filter(entry => entry.state === 'failed')
  }

  /** The open value panel — the same one both single dialogs use. */
  readonly picker = new ValuePicker()

  /** The open calendar. A date cell types as text, so its picker is this rather than a native one. */

  /** Which cell the open calendar belongs to. */

  /**
   * How many Item columns each expanded collection shows, by collection name; absent is collapsed.
   *
   * View state rather than schema: the tran category declares the collection and the shape of one
   * of its lines, and how many lines are on screen is what the expand and plus glyphs asked for.
   */
  private readonly itemCounts = new Map<string, number>()

  /** Item columns by grid key, so a cell can be resolved back to the line it addresses. */
  private readonly itemColumns = new Map<string, BulkItemColumn>()

  /**
   * How many items a collection was showing when it was last closed.
   *
   * So that closing a collection and opening it again is the same collection: a fourth payment
   * added with the plus and typed into is as real as the three the record arrived with, and
   * re-opening at the count the selection ARRIVED with would hide it.
   */
  private readonly itemMemory = new Map<string, number>()

  /** The columns on screen, the bands over them, and the grid's own track list. */
  columns: BulkTransactionColumn[] = []
  /** How many fields are shown of how many the tran category declares. */
  fieldCountLabel = ''
  /** How many rows are shown of how many the selection holds. */
  rowCountLabel = ''

  /** One verdict per row, and the four readings of them the chrome shows. */
  verdicts: BulkRowVerdict[] = []
  tallies: BulkTally[] = []
  missingRequiredCount = 0
  missingLabel = ''
  changedCount = 0

  /** Why the last range gesture was declined, or null. Cleared by the next gesture or click. */
  rangeNotice: string | null = null

  /**
   * Row indexes, for the template to iterate.
   *
   * Indexes rather than projected rows: a row costs ~0.5ms and ~34KB to project, so the grid asks
   * for the ones it is about to draw and nothing else. See {@link BulkTransactionViewModel.rowAt}.
   */
  rowIndexes: number[] = []

  /**
   * What ag-grid holds per row: an index, and nothing else.
   *
   * The record behind it is projected when a cell asks for it, so a selection of 30,000 costs 30,000
   * small objects rather than 30,000 projections. Row identity is the index, which never changes —
   * this grid neither adds nor removes rows.
   */
  gridRows: BulkGridRow[] = []

  gridOptions: GridOptions | null = null

  /** Range selection; the clipboard module follows when the paste handler moves onto it. */
  readonly gridModules: Module[] = [RangeSelectionModule]

  private gridApi: GridApi | null = null
  private gridColumnApi: ColumnApi | null = null

  /** Which ag-grid cell the open value panel belongs to. */
  private gridPickerTarget: { index: number; field: string } | null = null

  /**
   * Per-row counts, and the running totals over them.
   *
   * `missing` opens from the scan that projects nothing; `changed` opens at zero by definition.
   * From the first edit onward only the edited row is re-counted and the totals move by the delta.
   */
  private missingByRow: number[] = []
  private changedByRow: number[] = []
  private rowsMissing = 0
  private rowsChanged = 0

  /** Rows written during a range gesture, repainted once each when it ends; null outside one. */
  private pendingRefresh: Set<number> | null = null

  /** Typed lookup text, before debouncing. */
  private readonly typed = new Subject<LookupRequest>()

  private readonly lookups = new Subscription()

  /**
   * The lookup cell being typed into, so a reply for a cell the user has already left is dropped
   * rather than opening a panel over whatever they are doing now.
   */
  private lookupCell: { index: number; field: string } | null = null

  /** The dialog's once-per-(field, column) log for lookup rows missing a display column. */
  private readonly blankCells = blankCellLog()

  constructor(
    readonly activeModal: NgbActiveModal,
    private readonly host: ElementRef<HTMLElement>,
    private readonly changeDetector: ChangeDetectorRef,
    @Inject(DOCUMENT) private readonly document: Document,
    /**
     * The panel's injector, which carries the providers the host declares — the search service among
     * them. Looked up when a search is asked for rather than injected, the way the host resolves the
     * derivation seam: a grid that never searches never needs it.
     */
    private readonly injector: Injector,
  ) {
    this.lookups.add(
      this.typed
        .pipe(
          // Per keystroke would be one request per character. The pause is the user stopping, which
          // is the only moment the text is worth asking about.
          debounceTime(LOOKUP_DEBOUNCE_MS),
          distinctUntilChanged(
            (before, after) =>
              before.index === after.index &&
              before.field === after.field &&
              before.query === after.query,
          ),
          // `switchMap`, so a reply for text the user has already moved past is DROPPED rather than
          // racing the newer one into the panel.
          switchMap(request =>
            this.injector
              .get(HttpDialogListValuesService)
              .search(request.keyName, request.query, request.accountId)
              .pipe(map(result => ({ request, result }))),
          ),
        )
        .subscribe(({ request, result }) =>
          this.onLookupResult(request, result),
        ),
    )
  }

  ngOnDestroy(): void {
    this.lookups.unsubscribe()
    this.picker.close()
  }

  /** Assigned by the host immediately after `NgbModal.open`. */
  set vm(model: BulkTransactionViewModel | null) {
    this.model = model
    // A refusal is a compact dialog, not the grid's wide window. The host fixes `windowClass` when it
    // opens the modal, before it knows the selection will be refused, so the window is marked from
    // here; `bt-modal-window-blocked` is what narrows it.
    this.host.nativeElement
      .closest('.bt-modal-window')
      ?.classList.toggle('bt-modal-window-blocked', !!model?.blocked)
    this.saveError = null
    this.rangeNotice = null
    this.rowIndexes =
      model && !model.blocked
        ? Array.from({ length: model.rowCount }, (_, index) => index)
        : []
    this.gridRows = this.rowIndexes.map(index => ({ index }))
    // A collection the selection already has lines for opens showing them, at the count of the
    // longest row: a transaction carrying two payments must not have its second hidden because
    // the one beside it carries only one.
    this.itemCounts.clear()
    this.itemMemory.clear()
    for (const [collection, lines] of Object.entries(
      model?.openingItems ?? {},
    )) {
      if (lines > 0) this.itemCounts.set(collection, lines)
    }
    this.relayout()
    this.resetCounts()
  }

  get vm(): BulkTransactionViewModel | null {
    return this.model
  }

  // ── Layout ─────────────────────────────────────────────────────────────────────────────────────

  /** Whether the grid is showing only the fields a transaction cannot be saved without. */
  get hideOptionalFields(): boolean {
    return this.set === 'required'
  }

  setHideOptionalFields(hide: boolean): void {
    const set: BulkFieldSet = hide ? 'required' : 'all'
    if (this.set === set) return
    this.set = set
    this.picker.close()
    this.relayout()
  }

  private relayout(): void {
    const all = this.model?.columns ?? []
    const shown =
      this.set === 'all' ? all : all.filter(column => column.required)
    this.columns = this.withItemColumns(shown)
    // Counted over the transaction's OWN fields: an Item column is a line of a collection the
    // count already includes once, so adding them here would inflate the same thing twice.
    this.fieldCountLabel = `${shown.length} of ${all.length} Fields`
    // No row filter exists, so the two halves agree — the reading is shaped for the one that will.
    this.rowCountLabel = `${this.rowCount} of ${this.rowCount} Rows`
    this.relayoutGrid()
  }

  /**
   * The shown columns, with each expanded collection's Item columns spliced in after its summary.
   *
   * The summary stays: it is the row's own reading of the collection — "No Items", or the total —
   * and the Item columns beside it are the lines that reading is of.
   */
  private withItemColumns(
    columns: readonly BulkTransactionColumn[],
  ): BulkTransactionColumn[] {
    this.itemColumns.clear()
    if (!this.itemCounts.size) return [...columns]
    // The line's shape is the tran category's, so one already-projected row answers for all of
    // them — the same reason the column list itself is built from row 0.
    const shapes = this.model?.rowAt(0)?.collections ?? []
    return columns.flatMap(column => {
      const count = this.itemCounts.get(column.field) ?? 0
      const shape = shapes.find(entry => entry.name === column.field)
      if (!count || !shape) return [column]
      const items: BulkTransactionColumn[] = []
      for (let line = 0; line < count; line++) {
        for (const item of bulkItemColumns(shape, line)) {
          if (item.item) this.itemColumns.set(item.field, item.item)
          items.push(item)
        }
      }
      return [column, ...items]
    })
  }

  /** How many Item columns a collection is showing — 0 when it is collapsed. */
  itemCountOf(collection: string): number {
    return this.itemCounts.get(collection) ?? 0
  }

  /** The expand glyph on a collection's header: show its lines inline, or put them away again. */
  toggleCollectionItems(collection: string): void {
    const open = this.itemCounts.get(collection) ?? 0
    if (open) {
      this.itemMemory.set(collection, open)
      this.itemCounts.delete(collection)
    } else {
      this.itemCounts.set(
        collection,
        Math.max(
          1,
          this.model?.openingItems[collection] ?? 0,
          this.itemMemory.get(collection) ?? 0,
        ),
      )
    }
    this.relayout()
  }

  /** The plus on an Item band: one more line's worth of columns, across every row at once. */
  addCollectionItem(collection: string): void {
    this.itemCounts.set(collection, this.itemCountOf(collection) + 1)
    this.relayout()
  }

  /**
   * The cross on an Item band: close the last item.
   *
   * It closes the columns and leaves the lines alone. Closing is not deleting — a transaction's
   * second payment is still its second payment when the column stops being shown, and wiping one
   * across a selection on a single press is not a thing this glyph should be able to do. The plus
   * brings it back with its values intact.
   */
  removeCollectionItem(collection: string): void {
    const left = this.itemCountOf(collection) - 1
    this.itemMemory.set(collection, Math.max(0, left))
    if (left > 0) this.itemCounts.set(collection, left)
    else this.itemCounts.delete(collection)
    this.relayout()
  }

  /** The line an Item column addresses, or null where the row has no line that far down yet. */
  private lineAt(index: number, item: BulkItemColumn): string[] | null {
    const collection = this.rowAt(index)?.collections.find(
      entry => entry.name === item.collection,
    )
    return collection?.rows[item.line] ?? null
  }

  /**
   * The list a cell offers, or null when this row has no such cell at all.
   *
   * ONE answer for a field of the transaction and for a value of one of its lines. The two live in
   * different places — `row.fields` and a collection's column — and every enum gesture (open the
   * panel, pick from it, copy, paste) used to look the cell up in `row.fields` alone. An Item cell
   * is keyed `otherPayments#0.payment`, which is not in there, so each of them found nothing and
   * returned without a word: the dropdown looked editable and did nothing. An empty list is an
   * answer (a closed set whose options have not arrived is still a picker); null is not.
   */
  private listAt(index: number, field: string): ValuePickerOption[] | null {
    const source = this.listSourceAt(index, field)
    return source ? source.listValues ?? [] : null
  }

  /**
   * The definition behind a cell's list — its options, the columns the panel lays them out in, and
   * what a lookup needs to search — or null when this row has no such cell at all.
   *
   * One answer for all of them, for the reason {@link listAt} gives: a gesture that found a cell's
   * options but missed its columns would open a panel the single dialog would have drawn differently.
   */
  private listSourceAt(index: number, field: string): ListSource | null {
    const row = this.rowAt(index)
    const item = this.itemColumns.get(field)
    if (item) {
      const collection = row?.collections.find(
        entry => entry.name === item.collection,
      )
      return collection?.columns[item.slot] ?? null
    }
    return row?.fields[field] ?? null
  }

  /** What any cell reads, whether it is a field of the transaction or a value of one of its lines. */
  private cellValueOf(index: number, field: string): string {
    const item = this.itemColumns.get(field)
    if (item) return this.lineAt(index, item)?.[item.slot] ?? ''
    return this.rowAt(index)?.fields[field]?.value ?? ''
  }

  /**
   * Whether a required cell is holding nothing — the state that blocks Save, marked where the work
   * is.
   *
   * An Item cell on a row with no such line is NOT a gap: the row is not short of a payment it was
   * never going to make. The line, and with it the obligation to fill its required columns, begins
   * when something is typed into it.
   */
  private gapAt(index: number, field: string): boolean {
    const item = this.itemColumns.get(field)
    if (item) {
      const line = this.lineAt(index, item)
      return !!line && (line[item.slot] ?? '').trim() === ''
    }
    const cell = this.rowAt(index)?.fields[field]
    return !!cell && cell.value.trim() === ''
  }

  /**
   * Write a value to whichever cell the key names — a field of the transaction, or a value of one of
   * its lines.
   *
   * The grid's one write entry. Typing, a paste, a clear and a pick from the panel all land here, so
   * none of them has to know which kind of cell it was aimed at.
   */
  private writeAnyCell(index: number, field: string, value: string): boolean {
    const item = this.itemColumns.get(field)
    return item
      ? this.writeItemValue(index, item, value)
      : this.writeCellValue(index, field, value)
  }

  /**
   * Write into a collection line, adding the line when the row does not have one that far down.
   *
   * A row gains a line the moment one of its Item cells is filled and not before, which is what
   * keeps the plus glyph from marking every transaction in the selection as changed.
   */
  private writeItemValue(
    index: number,
    item: BulkItemColumn,
    value: string,
  ): boolean {
    const row = this.rowAt(index)
    const collection = row?.collections.find(
      entry => entry.name === item.collection,
    )
    const column = collection?.columns[item.slot]
    if (!collection || !column) return false

    const settled = formatFieldValue(
      rawValueFrom(value, column.style),
      column.style,
    )
    // Nothing to write and no line to write it into: adding a blank line here would invent an
    // item the user never asked for, and count its other required columns against them.
    if (settled === '' && !collection.rows[item.line]) return false
    while (collection.rows.length <= item.line) {
      collection.rows.push(collection.columns.map(() => ''))
    }

    const line = collection.rows[item.line]
    if (line[item.slot] === settled) return false
    line[item.slot] = settled
    this.dropEmptiedLines(
      collection,
      row?.originalLines[collection.name]?.length ?? 0,
    )
    this.resummarise(collection, row?.fields[collection.name])
    this.recountRow(index)
    this.refreshRowCells(index)
    return true
  }

  /**
   * Drop the trailing lines this grid created and the user has since emptied.
   *
   * A line comes into being when one of its Item cells is filled, so emptying it again has to undo
   * that. Otherwise a line typed into and then cleared survives with its required columns blank,
   * blocks Save for the whole selection, and there is no gesture left that removes it — closing the
   * band only hides the one editor that could have. A paste that lands on a later item makes the
   * lines before it the same way, and they go with it once it is cleared.
   *
   * `opened` is how many lines the row ARRIVED with, which are never dropped, blank or not: they are
   * the record's, and a write elsewhere has no business deleting one.
   */
  private dropEmptiedLines(
    collection: EditTransactionCollection,
    opened: number,
  ): void {
    const emptied = (line: readonly string[]): boolean =>
      line.every(cell => cell.trim() === '')
    while (
      collection.rows.length > opened &&
      emptied(collection.rows[collection.rows.length - 1])
    ) {
      collection.rows.pop()
    }
  }

  /**
   * Re-run a collection's summary cell over its edited lines.
   *
   * The summary is projected once, from the lines the row opened with, so without this the cell
   * beside the Item columns goes on reading the opening total while the lines move — and with no
   * items modal in this grid it is the collection's only reading. The aggregation is the
   * projection's own, via the closure it put on the collection, so the cell shows what the dialog
   * would have shown for these lines. The edit dialog does the same after its items modal.
   */
  private resummarise(
    collection: EditTransactionCollection,
    summary: EditTransactionField | undefined,
  ): void {
    if (!summary || !collection.summarise) return
    const { rawValue, value } = collection.summarise(collection.rows)
    summary.rawValue = rawValue
    summary.value = value
    summary.status = deriveFieldStatus(summary)
  }

  /**
   * The ag-grid column tree, rebuilt when the Required/All switch changes which columns are shown.
   *
   * Through the api once the grid exists, so the switch does not tear the grid down and lose the
   * scroll position; through `gridOptions` before that, which is what the grid starts from.
   */
  private relayoutGrid(): void {
    const columnDefs = bulkGridColumnDefs(this.columns, {
      rowAt: index => this.rowAt(index),
      // Only over rows ALREADY projected: a cell asking whether it is edited must not be the thing
      // that projects the other 29,999. Posting has nothing to differ from, so nothing reads as
      // edited there however much is typed.
      cellValueOf: (index, field) => this.cellValueOf(index, field),
      gap: (index, field) => this.gapAt(index, field),
      edited: (index, field) => {
        if (this.model?.mode === 'post') return false
        const row = this.projected(index)
        if (!row) return false
        const item = this.itemColumns.get(field)
        if (item) {
          const opening = row.originalLines[item.collection]?.[item.line]
          return this.cellValueOf(index, field) !== (opening?.[item.slot] ?? '')
        }
        const cell = row.fields[field]
        return !!cell && cell.value !== row.original[field]
      },
      editValueOf: (index, field) => {
        // A line stores what it shows, so an Item cell arms on the value it reads — the same
        // bargain a date cell strikes, and for the same reason: there is no rawer form held.
        if (this.itemColumns.has(field)) return this.cellValueOf(index, field)
        const cell = this.rowAt(index)?.fields[field]
        return cell ? editValue(cell.rawValue, cell.style) : ''
      },
      setValue: (index, field, value) => this.writeAnyCell(index, field, value),
      searchLookup: (index, field, text, anchor) =>
        this.searchLookup(index, field, text, anchor),
      expanded: collection => this.itemCountOf(collection) > 0,
      toggleItems: collection => this.toggleCollectionItems(collection),
      addItem: collection => this.addCollectionItem(collection),
      removeItem: collection => this.removeCollectionItem(collection),
      verdictAt: index => this.verdictAt(index),
      toggleEnum: (index, field, anchor) =>
        this.toggleGridEnum(index, field, anchor),
      enumOpen: (index, field) =>
        this.picker.isOpen(this.pickerKey(String(index), field)),
    })
    if (this.gridApi) {
      this.gridApi.setColumnDefs(columnDefs)
      return
    }
    this.gridOptions = {
      columnDefs,
      rowHeight: AG_ROW_HEIGHT,
      headerHeight: AG_HEADER_HEIGHT,
      groupHeaderHeight: AG_BAND_HEIGHT,
      // The design has no sort, no filter and no row selection — it is a form laid out as a grid,
      // and every one of those would also force the lazy projection to materialise every record.
      suppressRowClickSelection: true,
      suppressCellSelection: false,
      enableRangeSelection: true,
      suppressMultiRangeSelection: true,
      suppressContextMenu: true,
      suppressMovableColumns: true,
      animateRows: false,
      // Identity is the index: this grid neither adds nor removes rows, so a stable id lets ag-grid
      // reuse row nodes across a column change instead of rebuilding them.
      getRowNodeId: (data: BulkGridRow) => String(data.index),
      // ag-grid 26 cannot type an Angular component as `ColDef.cellEditor`, so the editor is
      // referenced by name and registered here — the pattern the positions-2 grid uses for its
      // own filter component.
      frameworkComponents: {
        [BULK_CELL_EDITOR]: BulkCellEditorComponent,
      },
      /*
       * Clicking a cell arms it.
       *
       * BOTH of these are needed, and the first is the one that matters: `NfGridOptions` sets
       * `suppressClickEdit: true`, which stops a click — single OR double — from starting an edit at
       * all. That is the right default for a report grid, where cells are read and a stray click
       * must not open an editor; it is the wrong one here, where the grid IS the form. Without this
       * override the date, number, lookup and text cells render and refuse to edit, which is exactly
       * how it behaved.
       *
       * `singleClickEdit` then decides how MANY clicks: one, because reaching a value in a form
       * should not take two gestures.
       */
      suppressClickEdit: false,
      singleClickEdit: true,
      /*
       * NOT `stopEditingWhenCellsLoseFocus`, which ag-grid itself documents as "for inline
       * (non-popup) editors only".
       *
       * A date cell edits through `nf-datepicker`, and its calendar opens in a popup outside the
       * cell. `GridBodyCtrl.addStopEditingWhenGridLosesFocus` watches `focusout` and calls
       * `stopEditing()` the moment focus lands on anything without a tabindex — which the calendar
       * is. So the editor was torn down as the calendar opened, and because Northfork's
       * `isCancelAfterEnd` cancels while the control is still pristine, the value reverted too: the
       * cell "edited for a while and went back", with no calendar ever visible.
       *
       * No bulk grid in helios sets this (see bulk-cash-entry and the rest), and nothing here needs
       * it: the enum trigger stops editing explicitly, which is what this was covering.
       */
      // The panel is anchored to a cell, so a scroll moves the cell out from under it. It
      // re-measures rather than closing — the same rule the hand-rolled grid follows.
      onBodyScroll: () => this.picker.reanchor(),
      // A lookup's panel belongs to its editor: when the editor closes, whatever it was offering goes
      // with it.
      onCellEditingStopped: () => this.endLookup(),
      // An enum is not an ag-grid editor (its panel would blank the cell it is editing), so the
      // keys that would have armed one open the panel instead. This is what keeps the enum
      // reachable without the mouse.
      onCellKeyDown: event => {
        // ag-grid reports full-width rows through the same hook; this grid has none.
        if ('column' in event) this.onGridCellKeyDown(event)
      },
    }
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api
    this.gridColumnApi = event.columnApi
  }

  /**
   * How tall the grid is, in pixels.
   *
   * Stated explicitly rather than left to `height: 100%`, because a percentage height resolves
   * against the parent's COMPUTED height — and the modal body has none of its own, so the grid
   * collapsed to zero and rendered no rows at all. Sizing it from the row count also matches the
   * design, where the grid is as tall as the transactions in it, and makes the grid independent of
   * whatever layout the modal happens to use.
   */
  get gridHeight(): number {
    const content =
      AG_BAND_HEIGHT +
      AG_HEADER_HEIGHT +
      this.rowCount * AG_ROW_HEIGHT +
      AG_SCROLLBAR
    return Math.min(content, AG_MAX_HEIGHT)
  }

  get rowCount(): number {
    return this.model?.rowCount ?? 0
  }

  /** The row at an index ONLY if it has already been projected. Never forces one. */
  private projected(index: number): BulkTransactionRow | null {
    return this.model?.projectedAt?.(index) ?? null
  }

  /** The transaction at an index, projected on first ask. */
  rowAt(index: number): BulkTransactionRow | null {
    const model = this.model
    if (!model || model.blocked || index < 0 || index >= model.rowCount) {
      return null
    }
    return model.rowAt(index)
  }

  // ── Cells ──────────────────────────────────────────────────────────────────────────────────────

  /** A supplied value that breaks its own parameterised rule (a minimum word count). */
  requiredRuleBroken(field: EditTransactionField): boolean {
    return requiredRuleUnmet(field)
  }

  /** The collection a summary cell opens, or null for an ordinary field. */
  collectionFor(
    row: BulkTransactionRow,
    column: BulkTransactionColumn,
  ): EditTransactionCollection | null {
    if (column.kind !== 'collection') return null
    return row.collections.find(entry => entry.name === column.field) ?? null
  }

  // ── Row and grid state ─────────────────────────────────────────────────────────────────────────

  /**
   * Per-row counts, and the running totals over them.
   *
   * Held as counts rather than re-walked, because the four readings the chrome shows — the gutter
   * glyph, the three tallies, the missing-values note and the Save gate — are all functions of these
   * two numbers per row. Re-deriving them would mean projecting every transaction on every
   * keystroke, which is exactly the cost this phase exists to remove.
   *
   * `missing` opens from {@link BulkTransactionViewModel.openingMissing}, which is scanned without
   * projecting; `changed` opens at zero by definition. From the first edit onward only the edited
   * row is recomputed and the totals move by the delta.
   */
  private resetCounts(): void {
    const model = this.model
    const count = model && !model.blocked ? model.rowCount : 0
    this.missingByRow = model?.openingMissing.slice() ?? []
    this.changedByRow = new Array(count).fill(0)
    this.rowsMissing = this.missingByRow.filter(missing => missing > 0).length
    this.rowsChanged = 0
    this.changedCount = 0
    this.publish()
  }

  /** Re-count ONE row after an edit, and move the totals by the difference. */
  private recountRow(index: number): void {
    const row = this.rowAt(index)
    if (!row) return
    const counts = bulkRowCounts(row)
    const wasMissing = this.missingByRow[index] ?? 0
    const wasChanged = this.changedByRow[index] ?? 0
    if (wasMissing > 0 !== counts.missing > 0) {
      this.rowsMissing += counts.missing > 0 ? 1 : -1
    }
    if (wasChanged > 0 !== counts.changed > 0) {
      this.rowsChanged += counts.changed > 0 ? 1 : -1
    }
    this.missingByRow[index] = counts.missing
    this.changedByRow[index] = counts.changed
    this.changedCount += counts.changed - wasChanged
    this.publish()
  }

  /** The chrome's four readings, from the counts. */
  private publish(): void {
    const footerMissing = bulkFooterMissing(this.model?.footer ?? [])
    const inRows = this.missingByRow.reduce((total, n) => total + n, 0)
    this.missingRequiredCount = inRows + footerMissing
    this.tallies = bulkTallies(
      this.rowCount,
      this.rowsMissing,
      this.rowsChanged,
    )
    this.missingLabel = bulkMissingLabel(
      inRows,
      this.rowsMissing,
      footerMissing,
    )
  }

  /**
   * Which row a field object belongs to, or null for a footer field.
   *
   * By identity over the rows ALREADY projected — a field the user just edited is by definition one
   * of those, so this never forces a projection. A footer field belongs to the session rather than
   * to any row, and falls out as null.
   */
  private indexOfField(field: EditTransactionField): number | null {
    for (const index of this.rowIndexes) {
      const row = this.projected(index)
      if (row && row.fields[field.field] === field) return index
    }
    return null
  }

  // ── Clipboard over ag-grid ─────────────────────────────────────────────────────────────────────
  // The same Excel round trip the hand-rolled grid carries, over ag-grid's own ranges. The rules
  // are unchanged and still live in `bulk-clipboard.ts` — tabs across, newlines down, a value the
  // column cannot hold leaving that cell alone. Only the question "which cells are selected" moves,
  // from a `GridSelection` this dialog maintained to `getCellRanges()`.
  //
  // ag-grid's own ClipboardModule is deliberately NOT registered: its paste writes raw strings
  // through, with no per-column coercion, which is the whole reason the existing bulk modal wrote
  // its own `PasteService` on top of ranges rather than using it.

  /**
   * Whether a clipboard gesture belongs to the grid.
   *
   * Asked of ag-grid's own state rather than of `document.activeElement`: ag-grid tracks a focused
   * cell of its own, and the DOM's active element is not reliably inside the grid when it does —
   * measured, with `getFocusedCell()` reporting a cell while `activeElement` was still `BODY`. A
   * guard on the DOM would have refused the paste.
   *
   * A text control OUTSIDE the grid still wins, which is what keeps a paste into the footer's Notes
   * box from being taken by a range left selected behind it. An open cell editor wins too — it is
   * the thing being typed into.
   */
  private clipboardBelongsToGrid(): boolean {
    if (!this.gridRange()) return false
    if (this.gridApi?.getEditingCells().length) return false
    const active = this.document.activeElement
    if (
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement
    ) {
      return !!active.closest('.bt-ag-wrap')
    }
    return true
  }

  /**
   * Whether a Delete or Backspace belongs to the grid — a stricter question than the clipboard's.
   *
   * Copy lifts values out and leaves them where they were; Delete empties every writable cell in the
   * block, and the grid has no undo. So it asks one thing more: that focus is the grid's own, or
   * nobody's. This dialog's `document:keydown` listener is live wherever focus is on the page — a
   * Save or Cancel button, or the workbench behind the dialog, which `hs-modal-window` leaves
   * clickable — and a key meant for any of those must not empty a block left selected here.
   *
   * `BODY` counts as the grid's, for the case {@link clipboardBelongsToGrid} measured: ag-grid
   * holds a focused cell while `activeElement` is still `BODY`.
   */
  private gridOwnsKeyboard(): boolean {
    if (!this.clipboardBelongsToGrid()) return false
    const active = this.document.activeElement
    return (
      !active ||
      active === this.document.body ||
      !!active.closest('.bt-ag-wrap')
    )
  }

  /** The selected block, as row indexes and field names. Gutters dropped — see the set's docs. */
  private gridRange(): { r0: number; r1: number; fields: string[] } | null {
    const ranges = this.gridApi?.getCellRanges()
    if (!ranges?.length) return null
    const range = ranges[0]
    const start = range.startRow?.rowIndex ?? 0
    const end = range.endRow?.rowIndex ?? 0
    const fields = range.columns
      .map(column => column.getColId())
      .filter(id => !BULK_GUTTER_COLUMNS.has(id))
    if (!fields.length) return null
    return { r0: Math.min(start, end), r1: Math.max(start, end), fields }
  }

  /** Every data column on screen, in display order — what a block pastes outward across. */
  private displayedFields(): string[] {
    return (this.gridColumnApi?.getAllDisplayedColumns() ?? [])
      .map(column => column.getColId())
      .filter(id => !BULK_GUTTER_COLUMNS.has(id))
  }

  /**
   * Whether a range gesture would reach more rows than {@link RANGE_GESTURE_MAX_ROWS} — and if so,
   * says so, because a gesture that quietly did nothing reads as a broken one.
   */
  private refuseOversized(rows: number): boolean {
    if (rows <= RANGE_GESTURE_MAX_ROWS) return false
    this.rangeNotice =
      `That selection spans ${rows.toLocaleString()} rows; copy, paste and Delete take ` +
      `${RANGE_GESTURE_MAX_ROWS.toLocaleString()} at a time. Select fewer rows and repeat.`
    return true
  }

  private copyFromGrid(event: ClipboardEvent): void {
    this.rangeNotice = null
    const range = this.gridRange()
    if (!range) return
    if (this.refuseOversized(range.r1 - range.r0 + 1)) {
      // Left unhandled, the browser would put whatever text it can find on the clipboard instead,
      // and the user would paste something they never meant to copy.
      event.preventDefault()
      return
    }
    const block: string[][] = []
    for (let r = range.r0; r <= range.r1; r++) {
      block.push(range.fields.map(field => this.copyFieldText(r, field)))
    }
    event.clipboardData?.setData('text/plain', toClipboardText(block))
    event.preventDefault()
  }

  private pasteIntoGrid(event: ClipboardEvent): void {
    this.rangeNotice = null
    const range = this.gridRange()
    if (!range) return
    const grid = parseClipboardGrid(
      event.clipboardData?.getData('text/plain') ?? '',
    )
    if (!grid.length) return
    event.preventDefault()

    const single = isSingleValue(grid)
    // The rows the paste will WRITE: the whole selection for one value, the clipboard's own height
    // (clamped to the grid) for a block — which can run past the selection's last row.
    const rows = single
      ? range.r1 - range.r0 + 1
      : Math.min(grid.length, this.rowCount - range.r0)
    if (this.refuseOversized(rows)) return

    this.repaintOnce(() =>
      single
        ? // One value over the whole selection — `PasteService.singleValueMultiRangePaste`.
          this.writeAcross(range.r0, range.r1, range.fields, () => grid[0][0])
        : this.pasteBlockIntoGrid(range, grid),
    )
  }

  /**
   * A block anchored at the selection's top-left, written outward and clamped to the grid.
   *
   * Clamped, never grown: every row here is a transaction that already exists, so a clipboard
   * taller than the grid stops at the last row rather than inventing transactions.
   */
  private pasteBlockIntoGrid(
    range: { r0: number; fields: string[] },
    grid: readonly string[][],
  ): boolean {
    const displayed = this.displayedFields()
    const anchor = displayed.indexOf(range.fields[0])
    if (anchor < 0) return false
    let touched = false
    grid.forEach((line, dr) => {
      const r = range.r0 + dr
      if (r >= this.rowCount) return
      line.forEach((value, dc) => {
        const field = displayed[anchor + dc]
        if (field && this.writeGridCell(r, field, value)) touched = true
      })
    })
    return touched
  }

  /** Delete over a block empties every cell in it that can be written to. */
  private clearGridRange(): void {
    this.rangeNotice = null
    const range = this.gridRange()
    if (!range) return
    if (this.refuseOversized(range.r1 - range.r0 + 1)) return
    this.repaintOnce(() =>
      this.writeAcross(range.r0, range.r1, range.fields, () => ''),
    )
  }

  private writeAcross(
    r0: number,
    r1: number,
    fields: readonly string[],
    value: (r: number, field: string) => string,
  ): boolean {
    let touched = false
    for (let r = r0; r <= r1; r++) {
      for (const field of fields) {
        if (this.writeGridCell(r, field, value(r, field))) touched = true
      }
    }
    return touched
  }

  /**
   * One pasted cell. A value the column cannot hold leaves that cell alone rather than blanking a
   * value that was correct — `PasteService`'s `allowUpdateBasedOnType` rule.
   */
  private writeGridCell(r: number, field: string, value: string): boolean {
    const column = this.columns.find(entry => entry.field === field)
    const options = this.listAt(r, field)
    if (!column || !options) return false
    const coerced = coercePastedValue(value, column, options)
    if (coerced === undefined) return false
    return this.writeAnyCell(r, field, coerced)
  }

  /**
   * Run a range write with its repaints collected, then repaint each row it reached once.
   *
   * A cell write repaints its whole row (the gutter verdict and the edited-cell ink move with it),
   * so a Delete across forty columns repainted every row forty times. It repaints the rows it
   * WROTE rather than the selection, which is also what a block paste needs: it runs past the
   * selection's last row.
   */
  private repaintOnce<T>(write: () => T): T {
    const rows = new Set<number>()
    this.pendingRefresh = rows
    try {
      return write()
    } finally {
      this.pendingRefresh = null
      rows.forEach(index => this.refreshRowCells(index))
    }
  }

  /** What a cell puts on the clipboard: what it reads on screen, which is what a picker accepts. */
  private copyFieldText(r: number, field: string): string {
    const options = this.listAt(r, field)
    if (!options) return ''
    const value = this.cellValueOf(r, field)
    const column = this.columns.find(entry => entry.field === field)
    return column?.kind === 'enum' ? pickerLabel(options, value) : value
  }

  /** Enter or F2 on a focused enum cell opens its panel, as those keys arm every other kind. */
  private onGridCellKeyDown(event: CellKeyDownEvent): void {
    const keyboard = event.event as KeyboardEvent | null
    const key = keyboard?.key
    if (key !== 'Enter' && key !== 'F2') return
    const field = event.column?.getColId()
    const index = (event.data as BulkGridRow | undefined)?.index
    if (field == null || index == null) return
    if (this.columns.find(entry => entry.field === field)?.kind !== 'enum') {
      return
    }
    // ag-grid's cell-key event carries no element, so the cell comes off the keystroke's own
    // target — which is inside the focused cell by definition.
    const target = keyboard?.target
    const cell =
      target instanceof Element ? target.closest<HTMLElement>('.ag-cell') : null
    if (cell) this.toggleGridEnum(index, field, cell)
  }

  /**
   * Open the value panel on an ag-grid enum cell, or shut it if this cell's panel is already open.
   *
   * The SAME panel the hand-rolled grid uses, positioned by the same {@link ValuePicker} against the
   * same dialog root — the only thing that changes is which element it hangs off. ag-grid's own
   * popup-editor lifecycle is deliberately not used: it blanks the cell it is editing, and the
   * design keeps the value and its chevron visible while the list is open.
   */
  private toggleGridEnum(
    index: number,
    field: string,
    anchor: HTMLElement,
  ): void {
    const source = this.listSourceAt(index, field)
    if (!source) return
    // The one thing that closes an open editor when the value panel opens over it. A trigger's
    // `preventDefault` stops the press reaching the grid, so a cell armed a moment ago never blurs,
    // and the grid option that would have caught that is deliberately off — see `gridOptions`.
    this.gridApi?.stopEditing()
    this.picker.toggle(
      this.pickerKey(String(index), field),
      source.listValues ?? [],
      this.cellValueOf(index, field),
      anchor,
      this.host.nativeElement,
      // The list's own columns, so a catalog like Financing ID draws its header and one cell per
      // `displayColumns` entry, as it does in the single dialog.
      source.displayColumns,
    )
    this.gridPickerTarget = this.picker.open ? { index, field } : null
    // The chevron reports the panel's state, so the cell has to be redrawn to turn it over.
    this.refreshRowCells(index)
  }

  /**
   * Write a typed value into one cell, and settle it.
   *
   * The grid's single write path: ag-grid's `valueSetter` commits an edit through here, and so does
   * a paste. It runs the same arm/commit cycle the single-record dialog does — the typed form in,
   * the Display Style form back out — and re-counts the row it touched.
   *
   * Returns whether anything actually changed, which is what stops a cell armed and left alone from
   * being reported as an edit.
   */
  private writeCellValue(index: number, field: string, value: string): boolean {
    const row = this.rowAt(index)
    const cell = row?.fields[field]
    if (!row || !cell) return false

    const raw = rawValueFrom(value, cell.style)
    const settled = formatFieldValue(raw, cell.style)
    if (settled === cell.value) return false

    cell.rawValue = raw
    cell.value = settled
    cell.status = deriveFieldStatus(cell)
    this.recountRow(index)
    // The gutter's verdict is a fact about the row, not about any column, so it has to be told.
    this.refreshRowCells(index)
    return true
  }

  /** Repaint one row's cells — the gutter glyph and the edited-cell ink both move with a write. */
  private refreshRowCells(index: number): void {
    if (this.pendingRefresh) {
      this.pendingRefresh.add(index)
      return
    }
    const node = this.gridApi?.getRowNode(String(index))
    if (node) this.gridApi?.refreshCells({ rowNodes: [node], force: true })
  }

  /** The gutter's glyph for one row, from its counts alone. */
  verdictAt(index: number): BulkRowVerdict {
    return bulkRowVerdict(
      this.missingByRow[index] ?? 0,
      this.changedByRow[index] ?? 0,
    )
  }

  get saveBlocked(): boolean {
    return this.missingRequiredCount > 0
  }

  /**
   * How many transactions Save would send (PVAL-26440): every row on a post, the CHANGED rows on an
   * edit — an unchanged row is never sent. The typed diff at save time is the last word, and the
   * outcome reports what was actually sent.
   */
  get sendCount(): number {
    return this.model?.mode === 'post' ? this.rowCount : this.rowsChanged
  }

  get saveLabel(): string {
    const failed = this.failedRequests.length
    if (this.locked && failed) {
      return `Retry ${failed} failed request${failed === 1 ? '' : 's'}`
    }
    const verb = this.model?.mode === 'post' ? 'Post' : 'Save'
    const count = this.sendCount
    return `${verb} ${count} Transaction${count === 1 ? '' : 's'}`
  }

  /**
   * Whether a grid cell editor is open. Its typed value is only the editor's until ag-grid commits it
   * through the column's `valueSetter` — and this grid does not commit on focus loss — so an open
   * editor may hold the edit that makes Save possible, or the value it must send.
   */
  private get editorOpen(): boolean {
    return (this.gridApi?.getEditingCells().length ?? 0) > 0
  }

  /** Commit the open editor, so what Save reads is what the user typed. Never cancels. */
  private commitEditor(): void {
    if (this.editorOpen) this.gridApi?.stopEditing()
  }

  /** The primary button's `disabled`. */
  get saveDisabled(): boolean {
    if (this.frozen) return true
    if (this.locked) return this.failedRequests.length === 0
    // An open editor may hold the first change or the last required value: Save commits it, then
    // judges — see `save`.
    if (this.editorOpen) return false
    return this.saveBlocked || this.sendCount === 0
  }

  /** The primary button's tooltip. */
  get saveTitle(): string {
    if (this.saving) return 'Saving…'
    if (this.frozen) return 'The transactions are not ready to save'
    if (this.locked) return 'Re-send only the requests that failed'
    if (this.editorOpen) return 'Save every change in this grid'
    if (this.saveBlocked) return 'Fill the missing required values first'
    if (this.sendCount === 0) return 'Nothing has changed'
    return 'Save every change in this grid'
  }

  // ── Editing ────────────────────────────────────────────────────────────────────────────────────

  onEdit(field: EditTransactionField, value: string): void {
    const raw = rawValueFrom(value, field.style)
    field.rawValue = raw
    field.value = value == null ? '' : String(value)
    field.status = deriveFieldStatus({
      ...field,
      value: formatFieldValue(raw, field.style),
    })
    const index = this.indexOfField(field)
    if (index === null) this.publish()
    else this.recountRow(index)
  }

  /**
   * A required field holding nothing — the one thing that blocks Save, marked where the work is.
   *
   * The grid marks its own cells through `cellClassRules`; this is the footer's reading of the same
   * verdict, so the two cannot disagree about what counts as missing.
   */
  requiredGap(field: EditTransactionField): boolean {
    return requiredUnsatisfied(field)
  }

  /**
   * A footer value settles into its Display Style form when the box is left.
   *
   * `onEdit` deliberately keeps the TYPED string in `value` while the user is typing — reformatting
   * mid-keystroke fights the caret — so something has to settle it afterwards. Today's only footer
   * field is `default`-styled and reads back identically either way; this is here so a footer field
   * with a real style does not become the bug that discovers it.
   */
  onFooterCommit(field: EditTransactionField): void {
    field.value = formatFieldValue(field.rawValue, field.style)
    field.status = deriveFieldStatus(field)
    this.publish()
  }

  onFooterEdit(field: EditTransactionField, value: string): void {
    this.onEdit(field, value)
  }

  /** Clears a footer value back to unset, the affordance the design puts beside its label. */
  clearFooter(field: EditTransactionField): void {
    this.onEdit(field, '')
  }

  // ── Selection and clipboard ────────────────────────────────────────────────────────────────────
  // Excel's gestures over this grid, following the prototype's own (`mdlPick` / `mdlRngRect` / its
  // copy and paste handlers) and the existing bulk Create/Edit modal's `CopyService` /
  // `PasteService`. Click to select, Shift+click or drag to extend, Ctrl+C to lift a block out as
  // tab-separated rows, Ctrl+V to drop one in, Delete to empty one.
  //
  // Keyboard NAVIGATION is deliberately absent: every editable cell here is a live `<input>`, so
  // arrows, Tab and typing all belong to the caret. The prototype can hijack them because its cells
  // are inert until armed, and adopting that model is a change to how every cell renders rather
  // than an addition to this one.

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Delete' && event.key !== 'Backspace') return
    // A cell being typed into owns its own Backspace; a selected block is the spreadsheet gesture.
    // `gridOwnsKeyboard` draws that line, and a stricter one than copy and paste — see it.
    if (!this.gridOwnsKeyboard()) return
    event.preventDefault()
    this.clearGridRange()
  }

  // Typed `Event` rather than `ClipboardEvent`: `emitDecoratorMetadata` writes a decorated method's
  // parameter types out as real references at import time, and jsdom defines no `ClipboardEvent`.
  @HostListener('document:copy', ['$event'])
  onCopy(event: Event): void {
    if (this.clipboardBelongsToGrid())
      this.copyFromGrid(event as ClipboardEvent)
  }

  @HostListener('document:paste', ['$event'])
  onPaste(event: Event): void {
    if (this.clipboardBelongsToGrid())
      this.pasteIntoGrid(event as ClipboardEvent)
  }

  // ── Value panel ────────────────────────────────────────────────────────────────────────────────

  /** Dismissal for the value panel, and for the cell selection. */
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    this.rangeNotice = null
    this.picker.dismiss(event.target)
    // A dismissal that closed an ag-grid panel leaves its chevron pointing the wrong way until the
    // cell is told.
    if (this.gridPickerTarget && !this.picker.open) {
      const closed = this.gridPickerTarget
      this.gridPickerTarget = null
      this.refreshRowCells(closed.index)
    }
  }

  private pickerKey(rowId: string, field: string): string {
    return `${rowId}~${field}`
  }

  /**
   * A value picked from the panel.
   *
   * Keyed by row index rather than by a field object: an ag-grid cell is a value read through a
   * getter, not a live field the template holds a reference to.
   */
  pickValue(value: string): void {
    const target = this.gridPickerTarget
    this.picker.close()
    this.gridPickerTarget = null
    if (!target) return
    // A lookup is picked FROM an open editor, which still holds the text that was typed. Cancelled
    // first: left to commit as it closes, the editor writes that text over the pick, and an editing
    // cell is one the repaint below skips.
    if (this.lookupCell) {
      this.lookupCell = null
      this.gridApi?.stopEditing(true)
    }
    this.writeAnyCell(target.index, target.field, value)
    this.refreshRowCells(target.index)
  }

  // ── Lookups ────────────────────────────────────────────────────────────────────────────────────
  // A `search` cell is a text box that asks the resolve endpoint what the typed text matches and
  // offers the answers in the same panel an enum uses, as the Edit dialog's lookup does. It is NOT
  // an enum: the set is unbounded, so there is nothing to show until something is typed, and the
  // editor stays typeable — what was typed is the value if nothing is picked.

  /**
   * A keystroke in a lookup cell, reported by its editor.
   *
   * For a field that does not search, or text shorter than its `minChars`, nothing is asked and the
   * cell is plain free text — the single dialog's rule.
   */
  private searchLookup(
    index: number,
    field: string,
    text: string,
    anchor: HTMLElement,
  ): void {
    const source = this.listSourceAt(index, field)
    if (source?.minChars == null) return

    this.lookupCell = { index, field }
    const query = text.trim()
    if (query.length < source.minChars) {
      // Below the threshold there is nothing to ask and nothing to show: the panel shuts rather than
      // keeping the previous query's answers over text that no longer produced them.
      this.closeLookupPanel(index, field)
      return
    }
    this.typed.next({
      index,
      field,
      keyName: source.field,
      query,
      accountId: this.rowAt(index)?.accountId ?? '',
      anchor,
    })
  }

  private onLookupResult(request: LookupRequest, result: ResolvedLists): void {
    const { index, field, keyName, anchor } = request
    // The user has moved on: a reply for a cell that is no longer being typed into is dropped.
    if (this.lookupCell?.index !== index || this.lookupCell.field !== field) {
      return
    }

    const failure = result.error ?? result.failures.get(keyName)
    if (failure != null) {
      // Logged, not shown: a lookup that cannot search is still a usable text box, and stopping the
      // grid over one field would cost the user every other cell in it.
      console.error(
        `[transactions-2] lookup search failed for ${keyName}; the cell stays free text`,
        failure,
      )
      this.closeLookupPanel(index, field)
      return
    }

    // Read by the field's own response keys, exactly as a catalog's rows were at projection time.
    const source = this.listSourceAt(index, field)
    const options = source?.listKeys
      ? normalizeOptions(
          result.options.get(keyName) ?? [],
          source.listKeys,
          this.blankCells(keyName),
        )
      : []
    if (!options.length) {
      this.closeLookupPanel(index, field)
      return
    }

    this.picker.show(
      this.pickerKey(String(index), field),
      options,
      anchor,
      this.host.nativeElement,
      source?.displayColumns ?? [],
    )
    this.gridPickerTarget = { index, field }
  }

  private closeLookupPanel(index: number, field: string): void {
    if (!this.picker.isOpen(this.pickerKey(String(index), field))) return
    this.picker.close()
    this.gridPickerTarget = null
  }

  /**
   * The editor closed — by Enter, Tab, Escape or a click on another cell — and whatever it was
   * offering goes with it.
   */
  private endLookup(): void {
    const cell = this.lookupCell
    if (!cell) return
    this.lookupCell = null
    this.closeLookupPanel(cell.index, cell.field)
  }

  // ── Save ───────────────────────────────────────────────────────────────────────────────────────

  /**
   * Save — for real (PVAL-26440).
   *
   * Assembles every request up front (`assembleBulkRequests`: grouped by account, chunked at
   * `SAVE_CHUNK_SIZE`) and freezes each body, then delivers them: an account's chunks one after
   * another, accounts side by side. Each request's outcome is recorded on its own.
   *
   *  - every request succeeds → the dialog closes with every created command;
   *  - some succeed → the dialog stays open, read-only, listing the accounts that failed, and the
   *    primary button becomes "Retry N failed requests", which re-sends those frozen bodies only;
   *  - none succeeds → the dialog is editable again with normal Save, and says why.
   *
   * After a partial success this is the retry: a fresh assembly would re-send rows that already
   * created commands.
   */
  save(): void {
    const model = this.model
    // `frozen` covers a save in flight AND the read freeze (still loading, or the read failed).
    if (!model || model.blocked || this.frozen) return
    if (this.locked) {
      this.retryFailed()
      return
    }
    // The open editor's value is the user's latest edit; commit it before anything is judged.
    this.commitEditor()
    if (this.saveBlocked || this.sendCount === 0) return
    this.saveError = null

    const changed = this.changedByRow.flatMap((count, index) =>
      count > 0 ? [index] : [],
    )
    let batches: BulkSaveBatch[]
    try {
      batches = assembleBulkRequests(model, changed)
    } catch (error) {
      if (!(error instanceof SaveAssemblyError)) throw error
      this.saveError =
        error.field === 'accountId'
          ? TRANSACTION_SAVE_COPY.noAccount
          : error.field === 'id'
          ? TRANSACTION_SAVE_COPY.noRecordId
          : `${error.label} does not hold a value that can be saved. Correct it and try again.`
      return
    }
    if (!batches.length) {
      this.saveError = TRANSACTION_SAVE_VALIDATION_COPY['transactionsRequired']
      return
    }
    this.requests = batches.map(batch => ({ ...batch, state: 'pending' }))
    this.deliver(this.requests)
  }

  /** Re-send the frozen bodies of the requests that failed, and nothing else. */
  retryFailed(): void {
    if (this.frozen) return
    const failed = this.failedRequests
    if (!failed.length) return
    this.deliver(failed)
  }

  /** How many transactions the requests that went through carried. */
  get sentCount(): number {
    return this.requests
      .filter(entry => entry.state === 'sent')
      .reduce((total, entry) => total + entry.request.transactions.length, 0)
  }

  /** Cancel, the header ✕ and — after a partial success — Close. Refused while a save is in flight. */
  dismiss(): void {
    if (this.saving) return
    if (this.locked) {
      // Some commands were created: the host hears about them, as it would on a full success.
      this.activeModal.close(this.sentResponses())
      return
    }
    this.activeModal.dismiss()
  }

  private sentResponses(): TransactionSaveResponse {
    return this.requests.flatMap(entry =>
      entry.state === 'sent' ? entry.response ?? [] : [],
    )
  }

  private deliver(entries: readonly BulkSaveRequestState[]): void {
    const model = this.model
    if (!model || !entries.length) return
    const service = this.injector.get(TransactionSaveService)
    this.saving = true
    this.errorHeader = undefined
    this.errorDetails = undefined

    const byAccount = new Map<string, BulkSaveRequestState[]>()
    entries.forEach(entry => {
      entry.state = 'pending'
      entry.failure = undefined
      byAccount.set(entry.accountId, [
        ...(byAccount.get(entry.accountId) ?? []),
        entry,
      ])
    })

    const perAccount = [...byAccount.values()].map(list =>
      // One account's chunks in order; a failed chunk does not stop the next — each is its own
      // request, and its outcome is recorded on its own.
      from(list).pipe(
        concatMap(entry =>
          service.save(entry.request, model.mode).pipe(
            tap(response => {
              entry.state = 'sent'
              entry.response = response
            }),
            catchError((error: unknown) => {
              entry.state = 'failed'
              entry.failure = saveFailureMessage(error)
              return EMPTY
            }),
          ),
        ),
      ),
    )

    merge(...perAccount)
      .pipe(finalize(() => this.settleDelivery()))
      .subscribe()
  }

  private settleDelivery(): void {
    this.saving = false
    const failed = this.failedRequests
    if (!failed.length) {
      this.activeModal.close(this.sentResponses())
      return
    }
    const detailed = failed.find(entry => entry.failure?.header)
    if (detailed?.failure) {
      this.errorHeader = detailed.failure.header
      this.errorDetails = detailed.failure.details
    }
    if (!this.locked) {
      // Nothing went through: the grid is the user's again, and a fresh Save re-assembles it. The
      // reasons stay on screen until then.
      this.saveError = [
        ...new Set(failed.map(entry => entry.failure?.message ?? '')),
      ]
        .filter(Boolean)
        .join(' ')
      this.requests = []
    }
    this.changeDetector.markForCheck()
  }
}
